/**
 * Phase 4.2b — Incoming Customer Call Modal.
 *
 * Surfaces when the WebRTC singleton has an `incomingCall` set (true
 * inbound from a customer; backend-mediated outbound bridges auto-answer
 * and never trip this UI).
 *
 * Minimal MVP — just Accept / Decline. Refinement (caller name lookup,
 * ringtone, lead context) lands in subsequent iterations.
 */
import React, { useEffect, useState } from 'react';
import { Phone, PhoneOff, Globe } from 'lucide-react';
import { getWebRTC, WEBRTC_INBOUND_ENABLED } from '../lib/webrtcClient';

const formatPhone = (e164) => {
  if (!e164) return 'Unknown caller';
  const m = String(e164).match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `(${m[1]}) ${m[2]}-${m[3]}`;
  return e164;
};

export default function IncomingCallModal() {
  const [state, setState] = useState(getWebRTC().getState());

  useEffect(() => {
    if (!WEBRTC_INBOUND_ENABLED) return undefined;
    const unsub = getWebRTC().subscribe((s) => setState(s));
    return unsub;
  }, []);

  if (!WEBRTC_INBOUND_ENABLED) return null;
  const inc = state.incomingCall;
  if (!inc) return null;

  const onAccept = () => {
    try { getWebRTC().acceptIncoming(); } catch (_) { /* swallow */ }
  };
  const onReject = () => {
    try { getWebRTC().rejectIncoming(); } catch (_) { /* swallow */ }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
      data-testid="incoming-call-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-[92vw] max-w-sm p-6 text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="inline-flex items-center gap-1.5 text-emerald-700">
            <Globe className="w-4 h-4" />
            <span className="text-[10px] font-semibold uppercase tracking-wide">On this computer</span>
          </span>
          <span className="h-3 w-px bg-slate-300" />
          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
            Incoming call
          </span>
        </div>

        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <Phone className="w-7 h-7 text-emerald-700 animate-pulse" />
        </div>

        <div
          className="text-xl font-semibold text-slate-900 mb-1"
          data-testid="incoming-call-from"
        >
          {formatPhone(inc.fromNumber)}
        </div>
        {inc.fromName ? (
          <div className="text-sm text-slate-500 mb-4">{inc.fromName}</div>
        ) : (
          <div className="text-sm text-slate-500 mb-4">is calling…</div>
        )}

        <div className="flex items-center justify-center gap-3 mt-6">
          <button
            type="button"
            onClick={onReject}
            className="inline-flex items-center gap-2 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium px-5 py-2.5 transition-colors"
            data-testid="incoming-call-reject-btn"
          >
            <PhoneOff className="w-4 h-4" />
            Decline
          </button>
          <button
            type="button"
            onClick={onAccept}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-5 py-2.5 transition-colors"
            data-testid="incoming-call-accept-btn"
          >
            <Phone className="w-4 h-4" />
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
